import React, { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useLiveMetrics } from "../hooks/useLiveMetrics";

/* ─── types ───────────────────────────────────────────────── */
interface Property { id: number; name: string; }
interface Unit     { id: number; unit_number: string; }

interface Meter {
  id: number;
  serial_number: string;
  unit: number;
  unit_number: string;
  property_name: string;
  status: "online" | "offline" | "fault";
  valve_open: boolean;
  last_seen: string | null;
  firmware_version: string | null;
  installed_at: string;
  latest_reading: {
    cumulative_value: string;
    flow_rate: string;
    pressure_bar: string;
    battery_percent: number;
  } | null;
}

/* ─── constants ───────────────────────────────────────────── */
const STATUS_META = {
  online:  { label: "Online",  bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-400", stripe: "from-emerald-400 to-teal-500",  icon: "#10b981" },
  offline: { label: "Offline", bg: "bg-slate-100",  text: "text-slate-500",   dot: "bg-slate-400",   stripe: "from-slate-300 to-slate-400",    icon: "#94a3b8" },
  fault:   { label: "Fault",   bg: "bg-rose-50",    text: "text-rose-600",    dot: "bg-rose-400",    stripe: "from-rose-400 to-red-500",       icon: "#ef4444" },
};

/* ─── helpers ─────────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-2xl ${className}`} />;
}

function Battery({ pct }: { pct: number }) {
  const color = pct >= 60 ? "bg-emerald-400" : pct >= 30 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-9 h-4 border-2 border-gray-300 rounded-sm flex-shrink-0">
        <div className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-1 h-2 bg-gray-300 rounded-r-sm" />
        <div className={`absolute left-0 top-0 bottom-0 rounded-sm ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[12px] text-gray-500 font-semibold tabular-nums">{pct}%</span>
    </div>
  );
}

function MiniTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] rounded-xl px-3 py-2 text-[11px] shadow-xl border border-white/10">
      <p className="text-sky-300/60 mb-0.5">{label}</p>
      <p className="text-white font-bold">{Number(payload[0].value).toFixed(3)} {payload[0].name}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TENANT METER VIEW — personal live dashboard
═══════════════════════════════════════════════════════════ */
function TenantMeterView({ meter }: { meter: Meter }) {
  const { reading, history, connected } = useLiveMetrics(meter.id);

  const live = reading ? {
    cumulative_value: reading.cumulative,
    flow_rate:        reading.flow_rate,
    pressure_bar:     reading.pressure,
    battery_percent:  100,
  } : meter.latest_reading;

  const meta    = STATUS_META[meter.status] ?? STATUS_META.offline;
  const battery = live?.battery_percent ?? 0;

  const flowData     = history.slice(-40).map((h) => ({ time: h.time, value: h.flow_rate }));
  const pressureData = history.slice(-40).map((h) => ({ time: h.time, value: h.pressure }));

  const lastSeen = meter.last_seen
    ? new Date(meter.last_seen).toLocaleTimeString("en-KE", { timeStyle: "short" })
    : "Never";

  return (
    <div className="space-y-5">

      {/* ── meter identity banner ── */}
      <div className="bg-gradient-to-r from-[#0f2557] to-[#1e3a8a] rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <p className="text-white/50 text-[12px] font-semibold uppercase tracking-widest">Your Water Meter</p>
              {connected && (
                <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-300 bg-emerald-400/20 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
                  LIVE
                </span>
              )}
            </div>
            <p className="text-white text-[22px] font-black leading-tight">{meter.property_name}</p>
            <p className="text-sky-300/70 text-[14px] mt-0.5">
              Unit <span className="font-bold text-sky-200">{meter.unit_number}</span>
              <span className="mx-2">·</span>
              <span className="font-mono text-sky-300/60">{meter.serial_number}</span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-full ${meta.bg} ${meta.text}`}>
              <span className={`w-2 h-2 rounded-full ${meta.dot} ${meter.status === "online" ? "animate-pulse" : ""}`} />
              {meta.label}
            </span>
            <span className={`text-[12px] font-bold px-3 py-1.5 rounded-full ${meter.valve_open ? "bg-emerald-400/20 text-emerald-300" : "bg-rose-400/20 text-rose-300"}`}>
              {meter.valve_open ? "💧 Water Flowing" : "🔴 Valve Closed"}
            </span>
          </div>
        </div>
      </div>

      {/* ── live readings grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: "Flow Rate", value: live ? parseFloat(live.flow_rate).toFixed(3) : "—",
            unit: "L/min", bg: "bg-sky-50", border: "border-sky-100", text: "text-sky-700",
            icon: <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
          },
          {
            label: "Pressure", value: live ? parseFloat(live.pressure_bar).toFixed(2) : "—",
            unit: "bar", bg: "bg-violet-50", border: "border-violet-100", text: "text-violet-700",
            icon: <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth={1.8} className="w-5 h-5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3" strokeLinecap="round"/></svg>,
          },
          {
            label: "Cumulative", value: live ? parseFloat(live.cumulative_value).toFixed(1) : "—",
            unit: "litres", bg: "bg-teal-50", border: "border-teal-100", text: "text-teal-700",
            icon: <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>,
          },
          {
            label: "Battery", value: `${battery}`, unit: "%",
            bg: battery >= 60 ? "bg-emerald-50" : battery >= 30 ? "bg-amber-50" : "bg-rose-50",
            border: battery >= 60 ? "border-emerald-100" : battery >= 30 ? "border-amber-100" : "border-rose-100",
            text: battery >= 60 ? "text-emerald-700" : battery >= 30 ? "text-amber-700" : "text-rose-700",
            icon: <svg viewBox="0 0 24 24" fill="none" stroke={battery >= 60 ? "#10b981" : battery >= 30 ? "#f59e0b" : "#ef4444"} strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="7" width="16" height="10" rx="2"/><path d="M22 11v2" strokeLinecap="round"/></svg>,
          },
        ].map((s) => (
          <div key={s.label} className={`bg-white rounded-2xl border ${s.border} p-5 shadow-sm hover:shadow-md transition-all`}>
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
              {s.icon}
            </div>
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">{s.label}</p>
            <p className={`text-[28px] font-black ${s.text} leading-none mt-1`}>{s.value}</p>
            <p className="text-[12px] text-gray-400 mt-0.5">{s.unit}</p>
          </div>
        ))}
      </div>

      {/* ── last seen ── */}
      <div className="flex items-center gap-2 text-[12.5px] text-gray-400">
        <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="w-4 h-4">
          <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3" strokeLinecap="round"/>
        </svg>
        Last data received: <span className="font-semibold text-gray-600">{lastSeen}</span>
        {!connected && <span className="ml-2 text-amber-500 font-semibold">· Waiting for live connection…</span>}
      </div>

      {/* ── live charts ── */}
      {history.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Flow rate chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[14px] font-bold text-gray-800">Live Flow Rate</p>
                <p className="text-[12px] text-gray-400 mt-0.5">Real-time L/min — last {flowData.length} readings</p>
              </div>
              <span className="text-[11px] font-bold text-sky-500 bg-sky-50 px-2.5 py-1 rounded-lg">L/min</span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={flowData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="flowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="time" hide />
                <YAxis hide />
                <Tooltip content={<MiniTooltip />} />
                <Area type="monotone" dataKey="value" name="L/min" stroke="#0ea5e9" strokeWidth={2} fill="url(#flowGrad)" dot={false} isAnimationActive={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Pressure chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[14px] font-bold text-gray-800">Live Pressure</p>
                <p className="text-[12px] text-gray-400 mt-0.5">Real-time bar — last {pressureData.length} readings</p>
              </div>
              <span className="text-[11px] font-bold text-violet-500 bg-violet-50 px-2.5 py-1 rounded-lg">bar</span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={pressureData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="time" hide />
                <YAxis domain={[0, 10]} hide />
                <Tooltip content={<MiniTooltip />} />
                <Line type="monotone" dataKey="value" name="bar" stroke="#7c3aed" strokeWidth={2} dot={false} isAnimationActive={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* no live data yet */}
      {history.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-sky-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.4} className="w-7 h-7">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="font-bold text-gray-700 text-[14px]">Waiting for live data</p>
          <p className="text-[12.5px] text-gray-400 mt-1">
            Charts will appear once your meter starts transmitting. This updates every 30 seconds.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── meter modal (create / edit) ────────────────────────── */
function MeterModal({ initial, properties, onClose, onSaved }: {
  initial?: Meter; properties: Property[]; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm]     = useState({ serial_number: initial?.serial_number ?? "", unit: initial?.unit?.toString() ?? "" });
  const [units, setUnits]   = useState<Unit[]>([]);
  const [selectedProp, setSelectedProp] = useState<string>(
    initial ? properties.find((p) => p.name === initial.property_name)?.id.toString() ?? "" : ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const isEdit = !!initial;

  useEffect(() => {
    if (!selectedProp) { setUnits([]); return; }
    api.get(`/properties/${selectedProp}/units/`)
      .then(({ data }) => setUnits(data.results ?? data))
      .catch(() => setUnits([]));
  }, [selectedProp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit && !form.unit) { setError("Please select a unit."); return; }
    setLoading(true); setError("");
    try {
      isEdit
        ? await api.patch(`/meters/${initial!.id}/`, { serial_number: form.serial_number })
        : await api.post("/meters/", { serial_number: form.serial_number, unit: parseInt(form.unit) });
      onSaved(); onClose();
    } catch (err: any) {
      const d = err.response?.data;
      setError(typeof d === "string" ? d : JSON.stringify(d) || "Failed to save.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0f2557] to-[#1e3a8a] px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-white font-black text-[15px]">{isEdit ? "Edit Meter" : "Register New Meter"}</h2>
            <p className="text-sky-300/60 text-[12px] mt-0.5">{isEdit ? `Updating ${initial!.serial_number}` : "Link an IoT device to a unit"}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-[12.5px] text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">{error}</p>}
          <div>
            <label className="block text-[11.5px] font-bold text-gray-500 uppercase tracking-widest mb-2">Serial Number</label>
            <input
              value={form.serial_number}
              onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              placeholder="e.g. SWB-KE-2024-0001"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-sky-300"
              required
            />
            <p className="text-[11px] text-gray-400 mt-1">Must match the serial in the device <code className="bg-gray-100 px-1 rounded">config.h</code></p>
          </div>
          {!isEdit && (
            <>
              <div>
                <label className="block text-[11.5px] font-bold text-gray-500 uppercase tracking-widest mb-2">Property</label>
                <select
                  value={selectedProp}
                  onChange={(e) => { setSelectedProp(e.target.value); setForm({ ...form, unit: "" }); }}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
                  required
                >
                  <option value="">Select a property…</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11.5px] font-bold text-gray-500 uppercase tracking-widest mb-2">Unit</label>
                <select
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  disabled={!selectedProp || units.length === 0}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-50"
                  required
                >
                  <option value="">{!selectedProp ? "Select property first" : units.length === 0 ? "No units available" : "Select a unit…"}</option>
                  {units.map((u) => <option key={u.id} value={u.id}>Unit {u.unit_number}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-3 rounded-xl bg-sky-500 text-white text-[13px] font-bold hover:bg-sky-600 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>Saving…</> : (isEdit ? "Save Changes" : "Register Meter")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── valve confirm ───────────────────────────────────────── */
function ValveConfirm({ meter, targetOpen, onConfirm, onCancel, loading }: {
  meter: Meter; targetOpen: boolean; onConfirm: () => void; onCancel: () => void; loading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center">
        <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${targetOpen ? "bg-emerald-50" : "bg-rose-50"}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke={targetOpen ? "#10b981" : "#ef4444"} strokeWidth={1.8} className="w-8 h-8">
            {targetOpen ? <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round"/> : <><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" strokeLinecap="round"/></>}
          </svg>
        </div>
        <h3 className="text-[15px] font-black text-gray-800 mb-1">{targetOpen ? "Open Valve?" : "Close Valve?"}</h3>
        <p className="text-[13px] text-gray-500 mb-6">
          This will {targetOpen ? "restore water flow" : "cut off water supply"} to <span className="font-bold text-gray-800">{meter.property_name} · Unit {meter.unit_number}</span>
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 py-2.5 rounded-xl text-white text-[13px] font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${targetOpen ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"}`}>
            {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>Sending…</> : (targetOpen ? "Open Valve" : "Close Valve")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── manager meter card ──────────────────────────────────── */
function ManagerMeterCard({ meter, onEdit, onRefresh }: { meter: Meter; onEdit: () => void; onRefresh: () => void; }) {
  const { reading, history, connected } = useLiveMetrics(meter.id);
  const [valveDialog, setValveDialog]   = useState<boolean | null>(null);
  const [valveLoading, setValveLoading] = useState(false);
  const [valveState, setValveState]     = useState(meter.valve_open);
  const [showChart, setShowChart]       = useState(false);

  const live = reading ? { cumulative_value: reading.cumulative, flow_rate: reading.flow_rate, pressure_bar: reading.pressure, battery_percent: 100 } : meter.latest_reading;
  const meta    = STATUS_META[meter.status] ?? STATUS_META.offline;
  const battery = live?.battery_percent ?? 85;
  const chartData = history.slice(-30).map((h) => ({ time: h.time, flow: h.flow_rate }));

  const handleValve = async () => {
    setValveLoading(true);
    try {
      await api.post(`/meters/${meter.id}/valve/`, { command: valveDialog ? "open" : "close", reason: "Manual control from dashboard" });
      setValveState(!!valveDialog);
      setValveDialog(null);
      onRefresh();
    } catch { } finally { setValveLoading(false); }
  };

  return (
    <>
      <div className={`bg-white rounded-2xl border shadow-sm hover:shadow-md overflow-hidden transition-all ${meter.status === "online" ? "border-emerald-100" : meter.status === "fault" ? "border-rose-100" : "border-gray-100"}`}>
        <div className={`h-1 bg-gradient-to-r ${meta.stripe}`} />
        <div className="p-5">
          {/* header */}
          <div className="flex items-start justify-between gap-3 mb-5">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={meta.icon} strokeWidth={1.8} className="w-5 h-5">
                  <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[14px] font-black text-gray-800 truncate">{meter.property_name}</p>
                  {connected && <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md flex-shrink-0"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping"/>LIVE</span>}
                </div>
                <p className="text-[12px] text-gray-400 truncate">Unit <span className="font-semibold text-gray-600">{meter.unit_number}</span> · <span className="font-mono">{meter.serial_number}</span></p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full ${meta.bg} ${meta.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${meter.status === "online" ? "animate-pulse" : ""}`}/>
                {meta.label}
              </span>
              <button onClick={onEdit} className="text-[11px] text-gray-400 hover:text-sky-500 transition-colors flex items-center gap-1 font-semibold">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round"/></svg>
                Edit
              </button>
            </div>
          </div>

          {/* readings */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label: "Flow", value: live ? parseFloat(live.flow_rate).toFixed(2) : "—", unit: "L/min", bg: "bg-sky-50", text: "text-sky-700" },
              { label: "Pressure", value: live ? parseFloat(live.pressure_bar).toFixed(2) : "—", unit: "bar", bg: "bg-violet-50", text: "text-violet-700" },
              { label: "Cumulative", value: live ? parseFloat(live.cumulative_value).toFixed(1) : "—", unit: "L", bg: "bg-teal-50", text: "text-teal-700" },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                <p className={`text-[18px] font-black ${s.text} leading-none`}>{s.value}</p>
                <p className={`text-[9.5px] font-bold text-gray-400 mt-0.5`}>{s.unit}</p>
                <p className="text-[9.5px] text-gray-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* battery + valve control */}
          <div className="flex items-center justify-between mb-4">
            <Battery pct={battery} />
            <span className="text-[11px] text-gray-400">Last: {meter.last_seen ? new Date(meter.last_seen).toLocaleTimeString("en-KE", { timeStyle: "short" }) : "—"}</span>
          </div>

          <div className={`rounded-xl border p-3 flex items-center justify-between ${valveState ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${valveState ? "bg-emerald-100" : "bg-rose-100"}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={valveState ? "#10b981" : "#ef4444"} strokeWidth={2} className="w-4 h-4">
                  {valveState ? <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round"/> : <><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" strokeLinecap="round"/></>}
                </svg>
              </div>
              <div>
                <p className={`text-[12.5px] font-black ${valveState ? "text-emerald-700" : "text-rose-700"}`}>Valve {valveState ? "Open" : "Closed"}</p>
                <p className="text-[11px] text-gray-400">{valveState ? "Water flowing" : "Water cut off"}</p>
              </div>
            </div>
            <button
              onClick={() => setValveDialog(!valveState)}
              className={`px-3.5 py-2 rounded-xl text-[12px] font-black transition-all shadow-sm ${valveState ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}
            >
              {valveState ? "Close" : "Open"}
            </button>
          </div>

          {chartData.length > 0 && (
            <button onClick={() => setShowChart(!showChart)} className="w-full flex items-center justify-between text-[12px] font-semibold text-sky-500 hover:text-sky-600 py-2.5 mt-2 border-t border-gray-50 transition-colors">
              <span>{showChart ? "Hide chart" : "Show live chart"}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-4 h-4 transition-transform ${showChart ? "rotate-180" : ""}`}><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          )}
        </div>

        {showChart && chartData.length > 0 && (
          <div className="px-5 pb-5 border-t border-gray-50">
            <p className="text-[11px] text-gray-400 font-medium mt-3 mb-2">Flow Rate (L/min) — live</p>
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={chartData} margin={{ top: 2, right: 2, left: -24, bottom: 0 }}>
                <defs><linearGradient id={`fg${meter.id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.25}/><stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="time" hide /><YAxis hide />
                <Tooltip content={<MiniTooltip />} />
                <Area type="monotone" dataKey="flow" name="L/min" stroke="#0ea5e9" strokeWidth={1.5} fill={`url(#fg${meter.id})`} dot={false} isAnimationActive={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {valveDialog !== null && (
        <ValveConfirm meter={meter} targetOpen={valveDialog} onConfirm={handleValve} onCancel={() => setValveDialog(null)} loading={valveLoading}/>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE — role-aware entry point
═══════════════════════════════════════════════════════════ */
export default function MetersPage() {
  const { user }  = useAuthStore();
  const isTenant  = user?.role === "tenant";

  const [meters, setMeters]         = useState<Meter[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState<"all"|"online"|"offline"|"fault">("all");
  const [propFilter, setPropFilter] = useState("all");
  const [showModal, setShowModal]   = useState(false);
  const [editMeter, setEditMeter]   = useState<Meter | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    if (isTenant) {
      // Use dedicated endpoint: User → Tenancy → Unit → Meter (direct chain, no ORM join)
      api.get("/meters/my/")
        .then(({ data }) => setMeters([data]))   // returns a single meter object
        .catch(() => setMeters([]))
        .finally(() => setLoading(false));
    } else {
      Promise.all([api.get("/meters/"), api.get("/properties/")])
        .then(([m, p]) => {
          setMeters(m.data.results ?? m.data);
          setProperties(p.data.results ?? p.data);
        })
        .finally(() => setLoading(false));
    }
  }, [isTenant]);

  useEffect(() => { load(); }, [load]);

  /* ── TENANT VIEW ── */
  if (isTenant) {
    if (loading) return (
      <div className="space-y-5">
        <Skeleton className="h-36" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_,i) => <Skeleton key={i} className="h-28"/>)}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[...Array(2)].map((_,i) => <Skeleton key={i} className="h-48"/>)}</div>
      </div>
    );
    if (meters.length === 0) return (
      <div className="flex flex-col items-center justify-center h-80 text-center">
        <div className="w-20 h-20 mb-4 rounded-3xl bg-sky-50 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.4} className="w-10 h-10"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <p className="text-[16px] font-black text-gray-700">No meter assigned yet</p>
        <p className="text-[13px] text-gray-400 mt-1">Contact your property manager to assign a meter to your unit.</p>
      </div>
    );
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-[22px] font-black text-gray-800">My Water Meter</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Live readings from your IoT smart meter</p>
        </div>
        <TenantMeterView meter={meters[0]} />
      </div>
    );
  }

  /* ── MANAGER VIEW ── */
  const online    = meters.filter((m) => m.status === "online").length;
  const offline   = meters.filter((m) => m.status === "offline").length;
  const fault     = meters.filter((m) => m.status === "fault").length;
  const valveOpen = meters.filter((m) => m.valve_open).length;
  const propNames = ["all", ...Array.from(new Set(meters.map((m) => m.property_name)))];

  const filtered = meters.filter((m) => {
    const q = search.toLowerCase();
    return (
      (!search || m.serial_number.toLowerCase().includes(q) || m.property_name.toLowerCase().includes(q) || m.unit_number.toLowerCase().includes(q)) &&
      (statusFilter === "all" || m.status === statusFilter) &&
      (propFilter === "all" || m.property_name === propFilter)
    );
  });

  const STATUS_TABS = [
    { key: "all" as const,     label: "All",     count: meters.length },
    { key: "online" as const,  label: "Online",  count: online,  dot: "bg-emerald-400" },
    { key: "offline" as const, label: "Offline", count: offline, dot: "bg-slate-400" },
    { key: "fault" as const,   label: "Fault",   count: fault,   dot: "bg-rose-400" },
  ];

  return (
    <>
      <div className="space-y-7">
        {/* header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-black text-gray-800">Water Meters</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">
              {loading ? "Loading…" : `${meters.length} meter${meters.length !== 1 ? "s" : ""} · ${online} online · ${fault} fault`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {online > 0 && (
              <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-xl font-black">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"/>
                {online} live
              </span>
            )}
            <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-black px-4 py-2.5 rounded-xl shadow-sm transition-all">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><path d="M12 5v14M5 12h14" strokeLinecap="round"/></svg>
              Register Meter
            </button>
          </div>
        </div>

        {/* KPI strip */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_,i) => <Skeleton key={i} className="h-24"/>)}</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Online",      value: online,    sub: "Transmitting",    color: "bg-emerald-50", stroke: "#10b981" },
              { label: "Offline",     value: offline,   sub: "No signal",       color: "bg-slate-100",  stroke: "#94a3b8" },
              { label: "Fault",       value: fault,     sub: "Needs attention", color: "bg-rose-50",    stroke: "#ef4444" },
              { label: "Valves Open", value: valveOpen, sub: "Water flowing",   color: "bg-sky-50",     stroke: "#0ea5e9" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-all">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">{s.label}</p>
                  <p className="text-[28px] font-black text-gray-800 leading-none mt-1">{s.value}</p>
                  <p className="text-[11.5px] text-gray-400 mt-1">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Serial, property, unit…" className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white shadow-sm"/>
          </div>
          <select value={propFilter} onChange={(e) => setPropFilter(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300">
            {propNames.map((p) => <option key={p} value={p}>{p === "all" ? "All Properties" : p}</option>)}
          </select>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {STATUS_TABS.map((t) => (
              <button key={t.key} onClick={() => setStatusFilter(t.key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all ${statusFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {"dot" in t && <span className={`w-1.5 h-1.5 rounded-full ${"dot" in t ? (t as any).dot : ""}`}/>}
                {t.label}
                <span className={`text-[10.5px] px-1.5 py-0.5 rounded-md font-bold ${statusFilter === t.key ? "bg-gray-100 text-gray-600" : "bg-gray-200/80 text-gray-400"}`}>{t.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* grid */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[...Array(4)].map((_,i) => <Skeleton key={i} className="h-72"/>)}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.4} className="w-8 h-8"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <p className="font-black text-gray-700 text-[15px]">{search || statusFilter !== "all" || propFilter !== "all" ? "No meters match your filter" : "No meters registered yet"}</p>
            <p className="text-[13px] text-gray-400 mt-1.5 mb-5">{search || statusFilter !== "all" || propFilter !== "all" ? "Try adjusting your search." : "Register your first IoT water meter to get started."}</p>
            {!search && statusFilter === "all" && propFilter === "all" && (
              <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-black px-5 py-2.5 rounded-xl shadow-sm transition-all">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><path d="M12 5v14M5 12h14" strokeLinecap="round"/></svg>
                Register First Meter
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((m) => <ManagerMeterCard key={m.id} meter={m} onEdit={() => setEditMeter(m)} onRefresh={load}/>)}
          </div>
        )}
      </div>

      {showModal && <MeterModal properties={properties} onClose={() => setShowModal(false)} onSaved={load}/>}
      {editMeter && <MeterModal initial={editMeter} properties={properties} onClose={() => setEditMeter(null)} onSaved={load}/>}
    </>
  );
}
